import { v4 } from 'uuid';
import { format, parseISO } from 'date-fns';

import { listTyresApi as getTyresApi, inquiryApi } from '../../../api/endpoints';
import { listBrandsApi } from '../../../api/endpoints';
import { getSeasonsApi } from '../../../api/endpoints';

import seasonButtons from '../../../consts/seasonButtons';
import capitalize from '../../../lib/capitalize';
import ediErrorCodeHandler from '../../../lib/ediErrorCodeHandler';
import parseError from '../../../error/parseError';

const PER_PAGE = 30;

export const createApiSlice = (set, get) => ({
  fetchBrands: async () => {
    const { brandFilters } = get();
    if (brandFilters.length > 0) return;

    try {
      const apiBrands = await listBrandsApi();
      const brandsList = apiBrands.map((brand) => ({
        label: capitalize(brand.name),
        value: brand._id,
        selected: false,
      }));
      set({ brandFilters: brandsList });
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  },

  fetchSeasons: async (preselectedSeasonIds = []) => {
    try {
      const apiSeasons = await getSeasonsApi();
      const seasonBtns = seasonButtons.map((t) => {
        const season = apiSeasons.find((s) => s.name === t.name);
        return {
          ...t,
          _id: season?._id,
          selected: preselectedSeasonIds.includes(season?._id),
        };
      });
      set({ seasonFilters: seasonBtns });
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  },

  buildQuery: () => {
    const state = get();
    const {
      searchValue,
      page,
      sortValue,
      searchOnlyInStock,
      minPrice,
      maxPrice,
      seasonFilters,
      brandFilters,
      loadIndexFilters,
      speedRatingFilters,
      fuelEfficiencyFilters,
      widthFilters,
    } = state;

    const query = {
      limit: PER_PAGE,
      available: true,
      offset: (page - 1) * PER_PAGE,
      populate: 'brand, season',
    };

    if (searchValue.trim().length) {
      // Check if this is a bulk EAN search (multiple values separated by ||)
      if (searchValue.includes('||')) {
        const eans = searchValue
          .split('||')
          .map((e) => e.trim())
          .filter(Boolean);
        query['ean__isin'] = eans.join(',');
      } else {
        query['search'] = `catalogNumber__icontains:${searchValue.trim()}||dimensions__icontains:${searchValue.trim()}`;
      }
    }

    const selectedSeasons = seasonFilters
      .filter((s) => s.selected)
      .map((s) => s._id)
      .filter(Boolean);
    if (selectedSeasons.length > 0) {
      query['season__isin'] = selectedSeasons.join(',');
    }

    const selectedBrands = brandFilters.filter((b) => b.selected).map((b) => b.value);
    if (selectedBrands.length > 0) {
      query['brand__isin'] = selectedBrands.join(',');
    }

    // Sorting
    if (sortValue) {
      switch (sortValue) {
        case 'price_asc':
          query.order = 'grossPrice';
          break;
        case 'price_desc':
          query.order = '-grossPrice';
          break;
        case 'name_asc':
          query.order = 'name';
          break;
        case 'name_desc':
          query.order = '-name';
          break;
        case 'brand_asc':
          query.order = 'brand';
          break;
        case 'availability':
          query.order = '-quantity';
          break;
        default:
          break;
      }
    }

    if (searchOnlyInStock) {
      query['quantity__gt'] = '0';
    }

    if (minPrice) {
      query['grossPrice__gte'] = minPrice;
    }

    if (maxPrice) {
      query['grossPrice__lte'] = maxPrice;
    }

    const selectedLoadIndexes = loadIndexFilters.filter((f) => f.selected).map((f) => f.value);
    if (selectedLoadIndexes.length > 0) {
      query['load__isin'] = selectedLoadIndexes.join(',');
    }

    const selectedSpeedRatings = speedRatingFilters.filter((f) => f.selected).map((f) => f.value);
    if (selectedSpeedRatings.length > 0) {
      query['speed__isin'] = selectedSpeedRatings.join(',');
    }

    const selectedFuelEfficiencies = fuelEfficiencyFilters
      .filter((f) => f.selected)
      .map((f) => f.value);
    if (selectedFuelEfficiencies.length > 0) {
      query['fuel__isin'] = selectedFuelEfficiencies.join(',');
    }

    // Width filters
    const selectedWidths = widthFilters.filter((f) => f.selected).map((f) => f.value);
    if (selectedWidths.length > 0) {
      const patterns = selectedWidths.map((w) => `dimensions__istartswith:${w}`);
      const existingSearch = query['search'];
      query['search'] = existingSearch
        ? `${existingSearch}||${patterns.join('||')}`
        : patterns.join('||');
    }

    return query;
  },

  fetchTyres: async ({ refresh = false, pageOverride } = {}) => {
    const apiId = v4();
    set({ _apiId: apiId, loading: true });

    if (refresh) {
      set({ tyres: [], page: 1 });
    }

    if (pageOverride) {
      set({ page: pageOverride });
    }

    try {
      const query = get().buildQuery();
      const results = await getTyresApi(query);

      // Check if this is still the current request - if not, let the newer request handle state
      if (get()._apiId !== apiId) {
        return;
      }

      set((s) => ({
        tyres: refresh ? results : [...s.tyres, ...results],
        loading: false,
        hasMore: results.length >= PER_PAGE,
      }));

      // Initialize filters from results
      if (results.length > 0) {
        get().initializeFiltersFromResults(results);
      }
    } catch (error) {
      // Always reset loading on error so user can retry
      console.error('Error fetching tyres:', error);
      // Reset page back so next attempt retries the same page
      const currentPage = get().page;
      set({ loading: false, page: Math.max(1, currentPage - 1) });
    }
  },

  loadMore: () => {
    const { loading, hasMore, page } = get();
    if (loading || !hasMore) return;
    
    const nextPage = page + 1;
    set({ page: nextPage });
    get().fetchTyres({ pageOverride: nextPage });
  },

  createInquiry: async (tyre, requestedDeliveryDate = null) => {
    try {
      const orderLineRequest = {
        catalogNumber: tyre.catalogNumber,
        rowQuantity: tyre.cartQuantity || 4,
      };

      if (requestedDeliveryDate) {
        orderLineRequest.requestedDeliveryDate = requestedDeliveryDate;
      }

      const response = await inquiryApi({
        inquiry: {
          orderLines: [orderLineRequest],
        },
      });

      const orderLine = response.OrderLine?.[0];
      const orderedArticle = orderLine?.OrderedArticle?.[0];
      const scheduleDetailsRaw = orderedArticle?.ScheduleDetails;
      const errorData = orderedArticle?.Error?.[0];
      const errorCode = errorData?.ErrorCode?.[0];
      const processedError = ediErrorCodeHandler(errorCode);

      if (!scheduleDetailsRaw || scheduleDetailsRaw.length === 0) {
        return { deliveryDate: null, scheduleDetails: [], error: processedError, isNotInStock: false, notInStockQuantity: null };
      }

      let notInStockQuantity = 0;
      const parsedSchedules = scheduleDetailsRaw
        .map((detail) => {
          const deliveryDateStr = detail.DeliveryDate?.[0];
          const quantityValue = detail.AvailableQuantity?.[0]?.QuantityValue?.[0];

          if (deliveryDateStr && quantityValue) {
            try {
              const parsedDate = parseISO(deliveryDateStr);
              const formattedDate = format(parsedDate, 'dd.MM.yyyy');
              const qty = parseInt(quantityValue, 10);
              if (formattedDate === '31.12.9999') {
                notInStockQuantity += qty;
                return null;
              }
              return {
                date: formattedDate,
                quantity: qty,
              };
            } catch {
              return null;
            }
          }
          return null;
        })
        .filter(Boolean);

      // If we had schedule details but all were 9999-12-31, mark as not in stock
      const isNotInStock = notInStockQuantity > 0 && parsedSchedules.length === 0;
      const deliveryDate = parsedSchedules.length > 0 ? parsedSchedules[0].date : null;

      return { 
        deliveryDate, 
        scheduleDetails: parsedSchedules, 
        error: processedError, 
        isNotInStock,
        notInStockQuantity: notInStockQuantity > 0 ? notInStockQuantity : null,
      };
    } catch (error) {
      return { error: parseError(error).message, deliveryDate: null, scheduleDetails: [], isNotInStock: false, notInStockQuantity: null };
    }
  },

  toggleTyreExpand: async (id) => {
    const { openTyreId, tyres, quantities, defaultQuantity } = get();

    if (openTyreId === id) {
      set({ openTyreId: null });
      return;
    }

    set({ openTyreId: id });

    const tyre = tyres.find((t) => t._id === id);
    if (!tyre || tyre.deliveryDate || tyre.errorMessage) return;

    get().updateTyre(id, { infoLoading: true });

    const result = await get().createInquiry({
      ...tyre,
      cartQuantity: quantities[id] || defaultQuantity,
    });

    get().updateTyre(id, {
      infoLoading: false,
      deliveryDate: result.deliveryDate,
      scheduleDetails: result.scheduleDetails || [],
      errorMessage: result.error,
      isNotInStock: result.isNotInStock,
      notInStockQuantity: result.notInStockQuantity,
    });
  },

  refreshDelivery: async (id) => {
    const { tyres, quantities, defaultQuantity } = get();
    const tyre = tyres.find((t) => t._id === id);
    if (!tyre) return;

    get().updateTyre(id, { infoLoading: true });

    const result = await get().createInquiry({
      ...tyre,
      cartQuantity: quantities[id] || defaultQuantity,
    });

    get().updateTyre(id, {
      infoLoading: false,
      deliveryDate: result.deliveryDate,
      scheduleDetails: result.scheduleDetails || [],
      errorMessage: result.error,
      isNotInStock: result.isNotInStock,
      notInStockQuantity: result.notInStockQuantity,
    });
  },

  setDeliveryDate: async (id, formattedDate) => {
    const { tyres, quantities, defaultQuantity } = get();
    const tyre = tyres.find((t) => t._id === id);
    if (!tyre) return;

    get().updateTyre(id, {
      selectedDeliveryDate: formattedDate,
      infoLoading: true,
      errorMessage: null,
    });

    const [day, month, year] = formattedDate.split('.');
    const apiDate = `${year}-${month}-${day}`;

    const result = await get().createInquiry(
      {
        ...tyre,
        cartQuantity: quantities[id] || defaultQuantity,
      },
      apiDate
    );

    get().updateTyre(id, {
      infoLoading: false,
      deliveryDate: result.deliveryDate,
      scheduleDetails: result.scheduleDetails || [],
      errorMessage: result.error,
      selectedDeliveryDate: result.error ? null : formattedDate,
      isNotInStock: result.isNotInStock,
      notInStockQuantity: result.notInStockQuantity,
    });
  },

  initialize: async (urlParams = {}) => {
    await get().fetchBrands();
    await get().fetchSeasons(urlParams.seasons ? urlParams.seasons.split(',') : []);
    if (urlParams.search) {
      set({ searchValue: urlParams.search });
    }
    await get().fetchTyres({ refresh: true });
  },
});
